import { ThunkDispatch } from '@reduxjs/toolkit';
import {
  closeNotiHistoryModal,
  getNotiHistoryModal,
  NotificationHistoryItem,
  openOrderDetailFromNoti,
  readAllNotis,
  readNoti
} from '@slice/orderManager.slice';
import { Button, Modal, Radio, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { RootState } from 'app/store';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isTicketTag } from './NotificationHistory';

const PAGE_SIZE = 20;

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(iso));

export default function NotificationHistoryModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();

  const { open, items, pageIndex, pageSize, totalRecords, loading } = useSelector(
    (state: RootState) => state.orderManager.notiHistoryModal!
  );

  const [tagFilter, setTagFilter] = useState<'all' | 'Order' | 'ProductTransaction'>('all');
  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('all');

  // Mở modal -> tải trang 1
  useEffect(() => {
    if (open) dispatch(getNotiHistoryModal({ pageIndex: 1, pageSize: PAGE_SIZE }));
  }, [open, dispatch]);

  const close = () => dispatch(closeNotiHistoryModal());

  // Chuyển sang QLĐH rồi mở luôn chi tiết (modal đơn nằm ở Layout, trang QLĐH không đọc ?maDonHang)
  const handleDetail = (maDonHang: string) => {
    close(); // đóng modal để thấy chi tiết đơn
    if (window.location.pathname !== '/quan-ly-don-hang') navigate('/quan-ly-don-hang');
    dispatch(openOrderDetailFromNoti(maDonHang));
  };

  const handleClickIdentifier = (item: NotificationHistoryItem, identifier: string) => {
    dispatch(readNoti(item.id));
    if (item.tag === 'Order') handleDetail(identifier);
    // Phiếu: identifier là id phiếu, TicketManager đọc ?ticketId rồi mở phiếu
    else if (isTicketTag(item.tag)) {
      close();
      navigate(`/quan-ly-phieu?ticketId=${identifier}`);
    } else {
      // TODO: chi tiết lệnh giao dịch (lệnh đã hủy => đã bị xóa, xử lý not found)
      console.warn('Chưa xử lý ProductTransaction:', identifier);
    }
  };

  const changePage = (page: number, size?: number) => {
    dispatch(getNotiHistoryModal({ pageIndex: page, pageSize: size ?? pageSize }));
  };

  // Lọc client-side TRONG TRANG hiện tại (API /Get chưa hỗ trợ lọc theo tag/đã đọc).
  const dataSource = useMemo(
    () =>
      items?.filter(
        i =>
          (tagFilter === 'all' || i.tag === tagFilter) &&
          (readFilter === 'all' || (readFilter === 'unread' ? !i.isRead : i.isRead))
      ),
    [items, tagFilter, readFilter]
  );

  const columns: ColumnsType<NotificationHistoryItem> = useMemo(() => {
    return [
      {
        title: t('Notification.ColContent'),
        key: 'content',
        render: (_, item) => (
          <div className="flex flex-col">
            <span className={`font-semibold ${item.isRead ? '' : 'text-red-600'}`}>{item.title}</span>
            {item.body && <span className="text-xs text-gray-500">{item.body}</span>}
          </div>
        )
      },
      {
        title: t('Notification.ColType'),
        dataIndex: 'tag',
        key: 'tag',
        width: 110,
        render: (tag: string) =>
          tag === 'Order' ? (
            <Tag color="blue">{t('Notification.TagOrder')}</Tag>
          ) : isTicketTag(tag) ? (
            <Tag color="cyan">{t('Notification.TagTicket')}</Tag>
          ) : (
            <Tag color="gold">{t('Notification.TagTransaction')}</Tag>
          )
      },
      {
        title: t('Notification.ColCode'),
        key: 'identifiers',
        render: (_, item) => (
          <Space size={[4, 4]} wrap>
            {item.identifiers?.map(id => (
              <Button
                key={id}
                size="small"
                type="link"
                className="!px-1"
                onClick={e => {
                  e.stopPropagation();
                  handleClickIdentifier(item, id);
                }}
              >
                {id}
              </Button>
            ))}
          </Space>
        )
      },
      {
        title: t('Notification.ColTime'),
        dataIndex: 'createDate',
        key: 'createDate',
        width: 150,
        render: (d: string) => <span className="text-xs">{formatDate(d)}</span>
      },
      {
        title: t('Notification.ColStatus'),
        dataIndex: 'isRead',
        key: 'isRead',
        width: 100,
        render: (isRead: boolean) =>
          isRead ? (
            <span className="text-gray-400">{t('Notification.Read')}</span>
          ) : (
            <Tag color="red">{t('Notification.Unread')}</Tag>
          )
      }
    ];
  }, [t]);

  return (
    <Modal
      visible={open}
      title={t('Notification.ModalTitle')}
      width={780}
      onCancel={close}
      destroyOnClose
      footer={[
        <Button key="readAll" onClick={() => dispatch(readAllNotis())}>
          {t('Notification.ReadAll')}
        </Button>,
        <Button key="close" type="primary" onClick={close}>
          {t('Notification.Close')}
        </Button>
      ]}
    >
      <div className="mb-3 flex flex-wrap items-center gap-4">
        <Radio.Group size="small" value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
          <Radio.Button value="all">{t('Notification.FilterAll')}</Radio.Button>
          <Radio.Button value="Order">{t('Notification.TagOrder')}</Radio.Button>
          <Radio.Button value="ProductTransaction">{t('Notification.TagTransaction')}</Radio.Button>
        </Radio.Group>

        <Radio.Group size="small" value={readFilter} onChange={e => setReadFilter(e.target.value)}>
          <Radio.Button value="all">{t('Notification.FilterAll')}</Radio.Button>
          <Radio.Button value="unread">{t('Notification.Unread')}</Radio.Button>
          <Radio.Button value="read">{t('Notification.Read')}</Radio.Button>
        </Radio.Group>
      </div>

      <Table<NotificationHistoryItem>
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        onRow={record => ({
          onClick: () => {
            if (!record.isRead) dispatch(readNoti(record.id));
          }
        })}
        pagination={{
          current: pageIndex,
          pageSize,
          total: totalRecords, // server-side: chỉ truyền data 1 trang + total -> antd không tự cắt
          showSizeChanger: false,
          showTotal: total => t('Notification.Total', { total }),
          onChange: changePage
        }}
      />
      {/* Lưu ý: 2 bộ lọc trên chỉ lọc trong TRANG hiện tại. Muốn lọc đúng toàn bộ,
          backend cần nhận thêm query (VD ?tag=Order&isRead=false) cho /Notification/Get. */}
    </Modal>
  );
}
